import type { ExtendedRecordMap } from "notion-types";

import type { ExcalidrawImperativeAPI } from "../../packages/excalidraw/types";
import type { ExcalidrawElement } from "../../packages/excalidraw/element/types";
import { randomId } from "../../packages/excalidraw/random";
import { getCommonBounds } from "../../packages/excalidraw/element/bounds";
import { newMindmapNodeElement } from "../../packages/excalidraw/element/newElement";
import { INTEGRATION_STYLE } from "../../packages/excalidraw/constants";
import { t } from "../../packages/excalidraw/i18n";

import {
  notionDatabase,
  notionOutline,
  notionPageRecordMap,
} from "../services/notion";

import {
  buildMindmapSkeleton,
  buildSkeletonFromOutline,
  isOutlineResponse,
  outlineHasUnresolvedWrapper,
} from "../../packages/mindmap-core/src/mindmapTree";
import type {
  MindmapTree,
  OutlineResponse,
} from "../../packages/mindmap-core/src/mindmapTree";
import { prefetchOutlineLevel } from "./mindmapExpand";
import { clearExpandInFlight, markExpandInFlight } from "./mindmapExpandBusy";
import {
  layoutMindmap,
  mindmapNodeHeight,
  mindmapNodeWidth,
} from "../../packages/mindmap-core/src/mindmapLayout";
import { emitMindmapElements } from "../../packages/mindmap-core/src/mindmapElements";
import { MINDMAP_NEUTRAL_COLOR } from "../../packages/mindmap-core/src/mindmapColors";
import type { Bounds } from "../../packages/mindmap-core/src/mindmapPlacement";
import {
  computeMapPlacement,
  viewportSceneCenter,
} from "../../packages/mindmap-core/src/mindmapPlacement";
import { buildDbMindmapTree } from "../../packages/mindmap-core/src/mindmapDbTree";
import type {
  Grouping,
  NotionDbRow,
} from "../../packages/mindmap-core/src/mindmapDbTree";
import { refreshMindmapFromNotion } from "./mindmapRefresh";
import { maybeNudgeLargeFlatDatabase } from "./mindmapRegroup";

/**
 * Orchestrator for the auto-mindmap tracer (issue #44).
 *
 * Fetches a Notion page's recordMap (already normalized by notionPageRecordMap),
 * builds the outline tree, lays it out, emits Excalidraw elements, and inserts
 * them into the scene in a *single* committed updateScene call — so one
 * generation is one undo step — then zooms to the new map.
 */
/**
 * Thrown when a page has no mappable blocks. Typed so the picker can show
 * "Nothing to map on this page" instead of a generic failure toast.
 */
export class EmptyMindmapError extends Error {
  constructor() {
    super("This Notion page has nothing to map.");
    this.name = "EmptyMindmapError";
  }
}

/**
 * Thrown when a database has no rows to map, so the picker can show "Nothing to
 * map in this database" instead of a generic failure toast.
 */
export class EmptyDatabaseMindmapError extends Error {
  constructor() {
    super("This Notion database has no rows to map.");
    this.name = "EmptyDatabaseMindmapError";
  }
}

/**
 * Bounding box of existing content to avoid, or null when there is nothing with
 * real geometry to collide with. Elements without numeric bounds (e.g. bare
 * stubs) are ignored so placement can't be thrown off by malformed input, and
 * tombstones are ignored so a new map never dodges invisible content.
 */
const occupiedBounds = (
  elements: readonly ExcalidrawElement[],
): Bounds | null => {
  const real = elements.filter(
    (el) =>
      !el.isDeleted &&
      Number.isFinite(el.x) &&
      Number.isFinite(el.y) &&
      Number.isFinite(el.width) &&
      Number.isFinite(el.height),
  );
  return real.length ? (getCommonBounds(real) as Bounds) : null;
};

/**
 * Marker written to `customData.mindmap` on a generation placeholder — the
 * temporary pill that holds the map's spot on canvas while the Notion fetch
 * runs, so the picker can close immediately instead of freezing on a grey
 * "Generating…" label. It deliberately carries NO map identity (no mapId, no
 * rootPageId), so the already-mapped check, refresh and expand all skip it.
 */
export interface MindmapPlaceholderData {
  placeholder: true;
}

/** Whether `el` is a generation placeholder rather than a real map node. */
export const isGenerationPlaceholder = (el: ExcalidrawElement): boolean =>
  el.customData?.mindmap?.placeholder === true;

/**
 * Drop a neutral "Building <title>…" pill where the map is about to land and
 * return its element id. Added with `commitToHistory: false` so the placeholder
 * never becomes its own undo step — the only history entry for the flow is the
 * final map commit, which also removes this pill.
 */
export const addGenerationPlaceholder = (
  excalidrawAPI: ExcalidrawImperativeAPI,
  title: string,
): string => {
  const label = t("mindmap.generatingPlaceholder", {
    title: title || "Untitled",
  });
  const treeLike: MindmapTree = {
    blockId: "mindmap-placeholder",
    text: label,
    kind: "block",
    hasChildren: false,
    children: [],
  };

  const pill = newMindmapNodeElement({
    x: 0,
    y: 0,
    width: mindmapNodeWidth(treeLike),
    height: mindmapNodeHeight(treeLike),
    ...INTEGRATION_STYLE,
    strokeColor: MINDMAP_NEUTRAL_COLOR,
    label,
    nodeKind: "block",
    branchColor: MINDMAP_NEUTRAL_COLOR,
    expandable: false,
    expanded: false,
  }) as ExcalidrawElement;

  // Including tombstones: `updateScene` replaces the scene wholesale, so
  // committing the live-only array would purge every deletion on the canvas.
  const existing = excalidrawAPI.getSceneElementsIncludingDeleted();
  // Same placement rule the real map will use, so the map lands on the spot the
  // placeholder occupied rather than jumping elsewhere on success.
  const { dx, dy } = computeMapPlacement(
    getCommonBounds([pill]) as Bounds,
    viewportSceneCenter(excalidrawAPI.getAppState()),
    occupiedBounds(existing),
  );
  const placed: ExcalidrawElement = {
    ...pill,
    x: pill.x + dx,
    y: pill.y + dy,
    customData: { mindmap: { placeholder: true } },
  };

  // No scrollToContent: the pill is placed at the viewport center by
  // construction, and fitting to a single small pill would zoom the canvas right
  // in, then straight back out when the map commits.
  excalidrawAPI.updateScene({
    elements: [...existing, placed],
    commitToHistory: false,
  });

  return placed.id;
};

/**
 * Take the placeholder back off the canvas without touching history — used when
 * generation fails, so the canvas is left exactly as it was.
 */
export const removeGenerationPlaceholder = (
  excalidrawAPI: ExcalidrawImperativeAPI,
  placeholderId: string,
): void => {
  const existing = excalidrawAPI.getSceneElementsIncludingDeleted();
  // A tombstoned placeholder is already off canvas — re-committing to drop it
  // would be a no-op commit.
  if (!existing.some((el) => el.id === placeholderId && !el.isDeleted)) {
    return;
  }
  excalidrawAPI.updateScene({
    elements: existing.filter((el) => el.id !== placeholderId),
    commitToHistory: false,
  });
};

/**
 * Refresh every map generated from `pageId` (a page can be mapped more than
 * once), sequentially so each refresh sees the previous one's scene. Lives here
 * rather than inline in the picker so any later entry point — e.g. a per-pill
 * hover affordance — can reuse the same "refresh this source" behavior.
 * `refreshMindmapFromNotion` reports each outcome via its own toast.
 */
export const refreshMapsForPage = async (
  excalidrawAPI: ExcalidrawImperativeAPI,
  mapIds: readonly string[],
  pageId: string,
): Promise<void> => {
  for (const mapId of mapIds) {
    await refreshMindmapFromNotion(excalidrawAPI, mapId, pageId);
  }
};

/**
 * What the picker already knows about a page before the click — its title and
 * icon. Enough to draw the map's real root pill immediately, so the user sees
 * their page on canvas at ~20 ms instead of a grey "Building <title>…" label
 * for the whole fetch.
 */
export interface MindmapRootMeta {
  title: string;
  icon?: { type: string; [key: string]: string } | null;
}

/**
 * The generation skeleton via the INCREMENTAL outline route, or null when that
 * route is unavailable for this page (permissions, an unsupported block, a bad
 * deploy) so the caller can fall back to the recordMap.
 *
 * One request. The recordMap route it replaces walked the whole page to
 * arbitrary depth — 218 blocks to paint six pills on a measured page — even
 * though generation only ever reads the root and level 1.
 */
const outlineSkeleton = async (
  pageId: string,
  fallbackTitle?: string,
): Promise<MindmapTree | null> => {
  try {
    const res = await notionOutline(
      { blockId: pageId, depth: 1, withTitle: true },
      { cache: true },
    );
    if (res.error || !isOutlineResponse(res.data)) {
      return null;
    }
    const outline: OutlineResponse = res.data;
    // See fetchOutline: an unresolved wrapper means a branch is missing from
    // this level with nothing to show for it, so fall back rather than generate
    // a map that is quietly incomplete.
    if (outlineHasUnresolvedWrapper(outline.children)) {
      return null;
    }
    return buildSkeletonFromOutline(
      outline,
      pageId,
      outline.title || fallbackTitle || "Untitled",
    );
  } catch {
    return null;
  }
};

/** The generation skeleton via the whole-page recordMap — the fallback route. */
const recordMapSkeleton = async (pageId: string): Promise<MindmapTree> => {
  const res = await notionPageRecordMap(
    { pageId, publicUrl: null },
    { cache: true },
  );

  if (res.error || !res.data) {
    throw new Error(
      `Failed to fetch Notion page ${pageId}: ${
        res.error?.message ?? "no recordMap returned"
      }`,
    );
  }

  // notionPageRecordMap already runs normalizeRecordMap, so consume res.data.
  return buildMindmapSkeleton(res.data as ExtendedRecordMap, pageId);
};

/**
 * Draw the map's real root pill now, from what the picker already knows, and
 * return the element ids it added.
 *
 * Uncommitted (`commitToHistory: false`) for the same reason the placeholder it
 * replaces was: the map commit that supersedes it is the flow's only history
 * entry, so the whole generation stays ONE undo step. It carries the real map
 * identity, so a failure has to take it back off (see `removeElements`) rather
 * than leave an orphan pill claiming to be a map.
 */
const paintRootPill = (
  excalidrawAPI: ExcalidrawImperativeAPI,
  pageId: string,
  meta: MindmapRootMeta,
  mapId: string,
  replacedIds: readonly string[],
  source: "page" | "database" = "page",
): string[] => {
  const tree: MindmapTree = {
    blockId: pageId,
    text: meta.title || "Untitled",
    // A database root is not a page: its id is a data_source id, so the side
    // peek must not offer to open it (mirrors buildDbMindmapTree).
    kind: source === "database" ? "database" : "page",
    pageId,
    icon: meta.icon ?? null,
    // Expandable, so the pill carries its chip from the first frame — and the
    // chip is where the level-below fetch reports itself (see the in-flight mark
    // in the callers). Whether there are really children is what the fetch is
    // about to say; if there are none it throws and this pill is removed.
    hasChildren: true,
    children: [],
  };
  const generated = emitMindmapElements(layoutMindmap(tree), tree, mapId, {
    source,
  });

  const existing = excalidrawAPI
    .getSceneElementsIncludingDeleted()
    .filter((el) => !replacedIds.includes(el.id));
  const { dx, dy } = computeMapPlacement(
    getCommonBounds(generated) as Bounds,
    viewportSceneCenter(excalidrawAPI.getAppState()),
    occupiedBounds(existing),
  );
  const placed = generated.map((el) => ({ ...el, x: el.x + dx, y: el.y + dy }));

  // No scrollToContent: fitting to one pill zooms right in, then straight back
  // out when the map commits.
  excalidrawAPI.updateScene({
    elements: [...existing, ...placed],
    commitToHistory: false,
  });

  return placed.map((el) => el.id);
};

/**
 * Spin the freshly painted root pill's chip while the level below it loads, and
 * return the id to clear. `null` when there is no pill (no picker metadata), so
 * the caller's `finally` stays a one-liner either way.
 *
 * The expand store, not a new one: this IS the map's first expand — the chip is
 * already wired to it, and reusing it means the placeholder-to-map handoff can
 * never leave two different busy states disagreeing.
 */
const markPillBusy = (ids: readonly string[]): string | null => {
  const id = ids[0] ?? null;
  if (id) {
    markExpandInFlight(id);
  }
  return id;
};

const clearPillBusy = (id: string | null): void => {
  if (id) {
    clearExpandInFlight(id);
  }
};

/** Take uncommitted elements back off the canvas, leaving history untouched. */
const removeElements = (
  excalidrawAPI: ExcalidrawImperativeAPI,
  ids: readonly string[],
): void => {
  const existing = excalidrawAPI.getSceneElementsIncludingDeleted();
  if (!existing.some((el) => ids.includes(el.id) && !el.isDeleted)) {
    return;
  }
  excalidrawAPI.updateScene({
    elements: existing.filter((el) => !ids.includes(el.id)),
    commitToHistory: false,
  });
};

export const generateMindmapFromPage = async (
  excalidrawAPI: ExcalidrawImperativeAPI,
  pageId: string,
  placeholderId?: string,
  rootMeta?: MindmapRootMeta,
): Promise<void> => {
  const mapId = `mindmap-${randomId()}`;
  const superseded = placeholderId ? [placeholderId] : [];
  // Only when the caller knows the title: a root pill labelled "Untitled" is no
  // better than the placeholder it would replace.
  const rootIds = rootMeta
    ? paintRootPill(excalidrawAPI, pageId, rootMeta, mapId, superseded)
    : [];
  // The root pill lands in ~20ms and then sits there looking finished for the
  // whole level-1 fetch. Spin its chip for the duration, the same feedback every
  // other expand gives, so "nothing is happening" reads as "children loading".
  const busyId = markPillBusy(rootIds);

  try {
    // V2 (issue #49): generation emits the skeleton — root + level-1 nodes only,
    // each level-1 node flagged expandable via hasChildren. Deeper levels arrive
    // one expand at a time.
    const tree =
      (await outlineSkeleton(pageId, rootMeta?.title)) ??
      (await recordMapSkeleton(pageId));
    if (tree.children.length === 0) {
      throw new EmptyMindmapError();
    }

    const layout = layoutMindmap(tree);
    const generated = emitMindmapElements(layout, tree, mapId, {
      source: "page",
    });

    commitGeneratedMap(excalidrawAPI, generated, [...superseded, ...rootIds]);
    // The map is on canvas; warm the level below it while the user reads, so
    // the first expand is a cache hit rather than another round trip.
    prefetchOutlineLevel(pageId);
  } catch (err) {
    // A partial map is worse than none: drop the root pill so a failure leaves
    // the canvas exactly as it was.
    removeElements(excalidrawAPI, rootIds);
    throw err;
  } finally {
    clearPillBusy(busyId);
  }
};

/**
 * Place a freshly emitted map at the viewport center (nudged clear of existing
 * content), insert it in a single committed `updateScene` — so one generation is
 * one undo step — and zoom to the new map. Shared by the page and database
 * orchestrators so both place and commit identically.
 *
 * `supersededIds` are the uncommitted stand-ins that held the spot while the
 * fetch ran — the placeholder pill and, on the page path, the instantly painted
 * root. They are dropped in the SAME commit that adds the map, so the swap is
 * atomic, leaves a single history entry, and the map never dodges its own
 * stand-in when placed.
 */
const commitGeneratedMap = (
  excalidrawAPI: ExcalidrawImperativeAPI,
  generated: ExcalidrawElement[],
  supersededIds: readonly string[] = [],
): void => {
  const existing = excalidrawAPI
    .getSceneElementsIncludingDeleted()
    .filter((el) => !supersededIds.includes(el.id));

  // Final translate: the origin-relative layout lands centered on the viewport,
  // nudged below/right if that space is already occupied by existing content.
  const { dx, dy } = computeMapPlacement(
    getCommonBounds(generated) as Bounds,
    viewportSceneCenter(excalidrawAPI.getAppState()),
    occupiedBounds(existing),
  );
  const placed = generated.map((el) => ({ ...el, x: el.x + dx, y: el.y + dy }));

  // One updateScene commit = one history entry for the whole generation.
  excalidrawAPI.updateScene({
    elements: [...existing, ...placed],
    commitToHistory: true,
  });

  excalidrawAPI.scrollToContent(placed, { fitToContent: true });
};

/**
 * Orchestrator for database → mindmap (issue #47), mirroring the page path:
 * fetch the rows, build the tree, lay it out, emit elements carrying
 * `source: "database"` + the grouping property, and insert them in a single
 * committed `updateScene`. Every row node opens its own Notion page.
 *
 * Generation never asks a question. An omitted `grouping` means FLAT — rows
 * straight off the database root — however many select/status properties the
 * database has. Guessing a grouping (or blocking on a chooser, as the picker
 * used to) made the first thing the user saw depend on a property they never
 * mentioned; grouping is now a deliberate choice on the root pill afterwards.
 */
export const generateMindmapFromDatabase = async (
  excalidrawAPI: ExcalidrawImperativeAPI,
  databaseId: string,
  databaseTitle: string,
  grouping?: Grouping,
  placeholderId?: string,
  rootMeta?: MindmapRootMeta,
): Promise<void> => {
  const mapId = `mindmap-${randomId()}`;
  const superseded = placeholderId ? [placeholderId] : [];
  // Same treatment as a page (V3 P8): the database's own pill, with its icon and
  // a spinning chip, rather than the grey "Building <title>…" stand-in. The
  // owner's "didn't we remove that?" — we had, but only on the page path.
  const rootIds = rootMeta
    ? paintRootPill(
        excalidrawAPI,
        databaseId,
        rootMeta,
        mapId,
        superseded,
        "database",
      )
    : [];
  const busyId = markPillBusy(rootIds);

  try {
    // Cached for the same reason as the page path: expanding a group re-queries
    // this database.
    const res = await notionDatabase(databaseId, { cache: true });

    if (res.error || !res.data?.results) {
      throw new Error(
        `Failed to fetch Notion database ${databaseId}: ${
          res.error?.message ?? "no rows returned"
        }`,
      );
    }

    const rows = res.data.results as NotionDbRow[];
    if (rows.length === 0) {
      throw new EmptyDatabaseMindmapError();
    }

    const tree = buildDbMindmapTree(
      { id: databaseId, title: databaseTitle || "Untitled" },
      rows,
      grouping,
    );
    if (tree.children.length === 0) {
      throw new EmptyDatabaseMindmapError();
    }

    const layout = layoutMindmap(tree);
    const generated = emitMindmapElements(layout, tree, mapId, {
      source: "database",
      groupingProperty: grouping?.propertyName,
    });

    commitGeneratedMap(excalidrawAPI, generated, [...superseded, ...rootIds]);
    // A big flat map is legible but unwieldy; point once at the control that
    // fixes it, after the map is on canvas rather than instead of it.
    maybeNudgeLargeFlatDatabase(excalidrawAPI, mapId);
  } catch (err) {
    // A lone root pill claiming to be a map is worse than nothing.
    removeElements(excalidrawAPI, rootIds);
    throw err;
  } finally {
    clearPillBusy(busyId);
  }
};
